import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import { usePerspective } from '../contexts/PerspectiveContext';
import {
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  Avatar,
  Chip,
  Button
} from '@mui/material';
import { MENU_TEXT_TO_MODULE_KEY, PATH_TO_MODULE_KEY } from '../constants/permissions';
import { usePermissions } from '../hooks/usePermissions';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  SpaceDashboard as SpaceDashboardIcon,
  Inventory as InventoryIcon,
  Warehouse as WarehouseIcon,
  ShoppingCart as OrdersIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Science as MaterialsIcon,
  AdminPanelSettings as AdminIcon,
  Group as GroupIcon,
  Store as StoreIcon,
  Assessment as AnalyticsIcon,
  LocalOffer as PromoIcon,
  PersonAdd as RepManagementIcon,
  Biotech as BiotechIcon,
  CalendarToday as CalendarIcon,
  SwapHoriz as TransferIcon,
  AccountBalance as AccountingIcon,
  Folder as FolderIcon,
  ManageSearch as AuditIcon,
  AccountCircle as UserSettingsIcon
} from '@mui/icons-material';

const drawerWidth = 280;

const InternalNavigation = ({ children, currentUser, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true); // Track desktop sidebar state
  const { org, clearOrg } = useOrg();
  const { clearPerspective } = usePerspective();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get feature flags - Enable EPL Purchase Orders
  const allowEplPOs = true; // Enable EPL purchase orders as requested
  
  const currentCompany = org; // Use org from context
  const { canAccessModule } = usePermissions(currentUser);

  // Route-level permission guard: redirect to dashboard if module is hidden
  useEffect(() => {
    if (!currentCompany || !location.pathname) return;
    const prefix = `/internal/${currentCompany}/`;
    if (!location.pathname.startsWith(prefix)) return;
    const pathAfterOrg = location.pathname.slice(prefix.length);
    // Find matching module key from the URL path
    const match = Object.entries(PATH_TO_MODULE_KEY).find(([pathSegment]) =>
      pathAfterOrg === pathSegment || pathAfterOrg.startsWith(pathSegment + '/')
    );
    if (match) {
      const moduleKey = match[1];
      if (!canAccessModule(moduleKey)) {
        navigate(`/internal/${currentCompany}/dashboard`, { replace: true });
      }
    }
  }, [location.pathname, currentCompany, canAccessModule, navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerClose = () => {
    setDesktopOpen(false);
  };

  const handleDesktopDrawerOpen = () => {
    setDesktopOpen(true);
  };

  const handleLogout = () => {
    clearOrg(); // Clear org on logout
    clearPerspective(); // Clear perspective on logout
    onLogout();
  };
  
  const handleSwitchOrg = () => {
    clearOrg();
    navigate('/org-select');
  };

  // Build menu items based on org and feature flags - ordered as requested
  const menuItems = [
    { text: 'Dashboard', icon: <SpaceDashboardIcon />, path: `/internal/${currentCompany}/dashboard` },
    { text: 'Order Processing', icon: <OrdersIcon />, path: `/internal/${currentCompany}/order-processing` },
    // Transfer Orders - EPL only (internal inventory transfers)
    ...(currentCompany === 'epl'
      ? [{ text: 'Transfer Orders', icon: <TransferIcon />, path: `/internal/${currentCompany}/transfer-orders` }]
      : []),
    { text: 'Raw Materials', icon: <MaterialsIcon />, path: `/internal/${currentCompany}/raw-materials` },
    // Donor Management - EPL only
    ...(currentCompany === 'epl'
      ? [{ text: 'Donor Management', icon: <BiotechIcon />, path: `/internal/${currentCompany}/donor-management` }]
      : []),
    { text: 'Receive Inventory', icon: <InventoryIcon />, path: `/internal/${currentCompany}/receive-inventory` },
    // EPL Calendar - EPL only
    ...(currentCompany === 'epl'
      ? [{ text: 'EPL Calendar', icon: <CalendarIcon />, path: `/internal/${currentCompany}/admin/calendar` }]
      : []),
    { text: 'Promo Codes', icon: <PromoIcon />, path: `/internal/${currentCompany}/admin/promo-codes` },
    { text: 'Client Management', icon: <GroupIcon />, path: `/internal/${currentCompany}/client-management` },
    { text: 'Rep Management', icon: <RepManagementIcon />, path: `/internal/${currentCompany}/admin/rep-management` },
    // Accounting - EPL only (invoices, pricing, product management)
    ...(currentCompany === 'epl'
      ? [{ text: 'Accounting', icon: <AccountingIcon />, path: `/internal/${currentCompany}/admin/accounting` }]
      : []),
    { text: 'Horses', icon: <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Box component="img" src={`${process.env.PUBLIC_URL}/Assets/horseVector.png`} alt="" sx={{ width: 14, height: 15, objectFit: 'contain' }} /></Box>, path: `/internal/${currentCompany}/horses` },
    { text: 'Reports', icon: <AnalyticsIcon />, path: `/internal/${currentCompany}/client-analytics` },
    { text: 'Resources', icon: <FolderIcon />, path: `/internal/${currentCompany}/admin/resources` },
    { text: 'Audit Logs', icon: <AuditIcon />, path: `/internal/${currentCompany}/admin/audit-logs` },
    // Show Purchase Orders based on org and feature flags
    ...(currentCompany === 'bare' || (currentCompany === 'epl' && allowEplPOs)
      ? [{ text: 'Purchase Orders', icon: <StoreIcon />, path: `/internal/${currentCompany}/purchase-orders` }]
      : []),
    { text: 'User Settings', icon: <UserSettingsIcon />, path: `/internal/${currentCompany}/user-settings` },
    { text: 'System Settings', icon: <SettingsIcon />, path: `/internal/${currentCompany}/settings` }
  ];

  // Filter menu items based on user permissions
  const filteredMenuItems = (() => {
    // Super admins bypass all permission checks
    if (currentUser?.isSuperAdmin === true ||
        (typeof currentUser?.organization === 'string' && currentUser.organization.toUpperCase() === 'BOTH')) {
      return menuItems;
    }

    // Get permissions from user profile
    const perms = currentUser?.profile?.permissions || currentUser?.permissions;

    // No structured permissions = show all (backward compatible)
    if (!perms || Array.isArray(perms) || !perms.modules) {
      return menuItems;
    }

    return menuItems.filter(item => {
      const moduleKey = MENU_TEXT_TO_MODULE_KEY[item.text];
      if (!moduleKey) return true; // Unknown items stay visible
      const modulePerm = perms.modules[moduleKey];
      if (!modulePerm) return true; // No explicit permission = visible
      return modulePerm.visible === true;
    });
  })();

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Unified Header Section - More Compact */}
      <Box sx={{ 
        p: 2, 
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        color: 'white',
        position: 'relative'
      }}>
        {/* Close button for desktop */}
        {!isMobile && (
          <IconButton
            onClick={handleDesktopDrawerClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
              zIndex: 1
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        )}
        {/* Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <AdminIcon sx={{ mr: 1.5, fontSize: 28, color: 'white' }} />
          <Box>
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 700, color: 'white', fontSize: '1rem' }}>
              Internal — {currentCompany === 'bare' ? 'BARE' : 'EPL'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.9)' }}>
              {currentCompany === 'bare' ? 'Bare Biologics' : 'EPL Operations'}
            </Typography>
          </Box>
        </Box>

        {/* User Info in Same Block */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar sx={{ mr: 1.5, bgcolor: 'rgba(255, 255, 255, 0.15)', color: 'white', width: 32, height: 32, border: '2px solid rgba(255, 255, 255, 0.3)', fontWeight: 600, fontSize: '0.9rem' }}>
            {(currentUser?.name || 'Admin').split(' ').map(n => n[0]).join('')}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'white', fontSize: '0.85rem' }}>
              {currentUser?.name || 'Demo Internal User'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.8)' }}>
              Operations Manager
            </Typography>
          </Box>
        </Box>

        {/* Access Badges */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Chip 
            label="Admin" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.15)', 
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              fontWeight: 600,
              fontSize: '0.65rem',
              height: '20px'
            }} 
          />
          <Chip 
            label="Live" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.15)', 
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              fontWeight: 600,
              fontSize: '0.65rem',
              height: '20px'
            }} 
          />
        </Box>
      </Box>

      <Divider />

      {/* Navigation Menu */}
      <List sx={{ px: 1.5, py: 0.5, flexGrow: 1 }}>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                borderRadius: 1,
                py: 0.75,
                px: 1.5,
                minHeight: 0,
                backgroundColor: location.pathname === item.path 
                  ? '#e3e8e3' 
                  : item.highlight 
                    ? 'rgba(92, 114, 92, 0.08)' 
                    : 'transparent',
                color: location.pathname === item.path 
                  ? '#5c725c' 
                  : item.highlight 
                    ? '#5c725c' 
                    : '#374151',
                border: item.highlight ? '1px solid rgba(92, 114, 92, 0.2)' : 'none',
                '&:hover': {
                  backgroundColor: location.pathname === item.path 
                    ? '#e3e8e3' 
                    : '#f6f7f6'
                }
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: location.pathname === item.path 
                    ? '#5c725c' 
                    : item.highlight 
                      ? '#5c725c' 
                      : '#6b7280',
                  minWidth: 32,
                  '& .MuiSvgIcon-root': { fontSize: '1.25rem' }
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontSize: '0.85rem', 
                  fontWeight: location.pathname === item.path || item.highlight ? 600 : 500 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Logout Section */}
      <Box sx={{ mt: 'auto', p: 1.5 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          size="small"
          sx={{ 
            mb: 1,
            bgcolor: '#ef4444',
            color: 'white',
            fontWeight: 600,
            fontSize: '0.8rem',
            py: 0.5,
            '&:hover': { 
              bgcolor: '#dc2626',
              transform: 'translateY(-1px)'
            }
          }}
        >
          Sign Out
        </Button>
        {(
          currentUser?.isSuperAdmin === true ||
          (typeof currentUser?.organization === 'string' && currentUser.organization.toUpperCase() === 'BOTH')
        ) && (
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={handleSwitchOrg}
            sx={{ 
              borderColor: '#039582',
              color: '#039582',
              fontSize: '0.75rem',
              py: 0.25,
              '&:hover': { 
                borderColor: '#027A6B',
                bgcolor: '#f0fdfa'
              }
            }}
          >
            Switch Organization
          </Button>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 1, bgcolor: '#f8fafc', borderTop: 1, borderColor: '#e2e8f0' }}>
        <Typography variant="body2" align="center" sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>
          © 2024 UnisynHub
        </Typography>
        <Typography variant="body2" align="center" sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
          {currentCompany === 'bare' ? 'Bare Biologics' : 'EPL'} Operations
        </Typography>
      </Box>
    </Box>
  );


  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>

      {/* Navigation Drawer */}
      <Box 
        component="nav" 
        sx={{ 
          width: 0, 
          flexShrink: 0,
          overflow: 'hidden'
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0
            }
          }}
        >
          {drawer}
        </Drawer>
        
        {!isMobile && desktopOpen && (
          <Drawer
            variant="permanent"
            open={desktopOpen}
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                position: 'fixed',
                height: '100vh',
                top: 0,
                left: 0,
                zIndex: 1200,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0
              }
            }}
          >
            {drawer}
          </Drawer>
        )}
        
        {/* Temporary drawer for closing animation on desktop */}
        {!isMobile && !desktopOpen && (
          <Drawer
            variant="temporary"
            open={false}
            sx={{
              display: 'none'
            }}
          />
        )}
      </Box>

      {/* Mobile Menu Button */}
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{ 
          position: 'fixed',
          top: 20,
          left: 20,
          zIndex: 1300,
          display: { md: 'none' },
          bgcolor: '#039582',
          color: 'white',
          '&:hover': { bgcolor: '#027A6B' }
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Desktop Menu Toggle Button (when sidebar is closed) */}
      {!isMobile && !desktopOpen && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDesktopDrawerOpen}
          sx={{ 
            position: 'fixed',
            top: 20,
            left: 20,
            zIndex: 1300,
            bgcolor: '#039582',
            color: 'white',
            '&:hover': { bgcolor: '#027A6B' }
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Main content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          width: '100%',
          marginLeft: { 
            xs: 0,
            md: desktopOpen ? `${drawerWidth}px` : 0 
          },
          transition: 'margin-left 0.3s ease',
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
          overflowY: 'auto'
        }}
      >
        <Box sx={{ p: 3, pt: { xs: 10, md: 3 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default InternalNavigation; 